import { dictionaryMerchants } from "./merchant-defaults"

/**
 * Merchant alias table.
 *
 * Maps variant merchant names to a canonical identifier.
 * Solves the problem fuzzy matching can't: "BUNDL TECHNOLOGIES" → "Swiggy",
 * "ANI TECHNOLOGIES" → "Ola", "ONE97" → "Paytm".
 *
 * Built from real statement data (HDFC, SBI, Amex, HDFC CCs).
 * Environment-agnostic: runs in WebWorker or server.
 */

/**
 * Each entry: canonical name → list of known aliases (substrings to match).
 *
 * Matching is case-insensitive substring: if the normalised merchant name
 * CONTAINS any alias, it resolves to the canonical name.
 *
 * Order matters — longer/more specific aliases should come first to avoid
 * ambiguous matches (e.g., "AMAZON PAY" before "AMAZON").
 */
const MERCHANT_ALIASES: [canonical: string, aliases: string[]][] = [
  // ── Food Delivery ──
  ["Swiggy", ["swiggy", "bundl technologies", "swiggy bundl"]],
  ["Zomato", ["zomato hyperpure", "zomato media", "zomato"]],
  ["EatClub", ["eatclubbrandspriv", "eatclub"]],

  // ── Quick Commerce ──
  ["Zepto", ["zepto marketplace", "zeptonow", "zepto"]],
  ["Blinkit", ["blink commerce", "blinkit"]],
  ["BigBasket", ["bigbasket innovative", "supermarket grocery", "bigbasket"]],

  // ── Ride Hailing ──
  ["Uber", ["uberindiasystem", "uber india systems", "uber bv", "uber trip", "uber"]],
  ["Ola", ["ani technologies", "ola electric", "ola cabs", "ola"]],
  ["Rapido", ["roppen transportation", "rapido"]],
  ["Shoffr", ["shoffrmobility", "shoffr"]],

  // ── E-Commerce ──
  [
    "Amazon",
    [
      "amazon seller services",
      "amazon pay india",
      "amazongiftcard",
      "amazon gift card",
      "amazon flights",
      "amazonawsesc",
      "amazon pay",
      "amazon.in",
      "amazon",
    ],
  ],
  [
    "Flipkart",
    ["flipkart internet", "flipkart payments", "fk advantage", "flipkart supermart", "flipkart"],
  ],
  ["Myntra", ["www myntra com", "myntra"]],
  ["Nykaa", ["fsn brands marketing", "nykaa e retail", "fsn brands", "nykaa"]],
  ["Meesho", ["fashnear technologies", "meesho"]],

  // ── Payments & Fintech ──
  [
    "CRED",
    ["dreamplug technologies", "cred.club", "cred club", "cred.utility", "cred.garage", "cred"],
  ],
  [
    "Paytm",
    [
      "one97 communications",
      "paytm payments",
      "paytm travel",
      "paytm train",
      "one97communicatio",
      "one97",
      "paytm",
    ],
  ],
  ["PhonePe", ["phonepe merchant", "phonepe"]],
  // "google play" is Play Store billing, not Google Pay: it lives in the dictionary (#322).
  ["Google Pay", ["google payment", "google india digital", "gpay"]],

  // ── Telecom ──
  ["Airtel", ["bharti airtel", "airtel payments bank", "www airtel in", "www.airtel.in", "airtel"]],
  ["Jio", ["reliance jio infocom", "reliance jio", "jio prepaid", "jio financial", "myjio", "jio"]],
  ["Vodafone", ["vodafone idea", "vi prepaid", "vodafone"]],

  // ── Travel ──
  [
    "IRCTC",
    [
      "indian railway cater",
      "irctc eticketing",
      "irctctourism",
      "irctc rail app",
      "irctc_ap",
      "irctc",
    ],
  ],
  ["Cleartrip", ["cleartrip"]],
  ["Ixigo", ["le travenues techn", "ixigo"]],
  ["MakeMyTrip", ["makemytrip india", "makemytrip"]],
  ["GoaMiles", ["www.goamiles.com", "goamiles", "goa miles"]],
  ["IndiGo", ["indigo airline", "indigo by iata", "interglobe aviation"]],
  ["Air India", ["wwwairindiaexpress", "air india express", "air india"]],
  ["Abhibus", ["abhibus"]],
  ["RedBus", ["redbus india", "redbus"]],

  // ── Entertainment ──
  ["BookMyShow", ["bigtree entertainment", "book my show", "bookmyshow"]],
  ["PVR INOX", ["pvr inox", "pvr cinemas", "pvr"]],
  ["Netflix", ["netflix.com", "netflix"]],
  ["Hotstar", ["hotstar", "disney plus"]],
  ["Spotify", ["spotify"]],
  ["YouTube Premium", ["youtube premium", "youtube prem"]],

  // ── Home Services ──
  ["Urban Company", ["urban company limite", "urbanclaptechnolo", "urbanclap", "urban company"]],
  ["Snabbit", ["snabbit"]],

  // ── Health & Pharmacy ──
  ["Tata 1mg", ["tata 1mg healthcare", "tata 1mg"]],
  ["Apollo Pharmacy", ["apollo pharmacy", "apollo clinic"]],

  // ── Retail ──
  ["DMart", ["avenue supermarts", "d mart", "dmart"]],
  ["Decathlon", ["decathlon sports india", "decathlon"]],
  ["Reliance Retail", ["relianceretailltd", "reliance retail"]],

  // ── Tata Group ──
  ["Tata Digital", ["tata digital priva", "tata digital"]],
  ["Tata Neu", ["tata neu"]],

  // ── Fashion ──
  ["H&M", ["h and m", "h m hennes", "h m"]],

  // ── Financial Services (not CC settlements — real expenses) ──
  ["Zerodha", ["zerodha broking", "zerodha"]],
  ["Groww", ["nextbillion technology", "groww"]],
  [
    "Indian Clearing Corporation",
    ["indian clearing corporation", "indian clearing corp", "nse clearing", "nse clearing mfss"],
  ],

  // ── Insurance (ensure these are NOT confused with CC settlements) ──
  ["HDFC Life", ["hdfc life insurance", "hdfc life", "hdfcli insuran"]],
  ["HDFC Ergo", ["hdfc ergo"]],
  ["SBI Life", ["sbi life insurance", "sbi life"]],
  ["ICICI Prudential", ["icici prudential", "icici pru"]],
  ["Bajaj Allianz", ["bajaj allianz"]],
  ["Tata AIA", ["tata aia"]],

  // ── Utilities ──
  ["BESCOM", ["bangalore electricit", "bescom"]],
  ["Adani Gas/Electricity", ["adani digital", "adani di"]],
  ["GAIL Gas", ["gail gas"]],

  // ── Others ──
  ["Airbnb", ["airbnb"]],
  ["Cursor", ["cursor ai powered", "cursor"]],
  ["GoDaddy", ["go daddy", "godaddy"]],
  ["Bangalore Metro", ["bengaluru metro", "bangalore metro"]],
  ["Mumbai Metro", ["maha mumbai metro", "mumbai metro"]],
  ["Delhi Metro", ["delhi metro"]],
  ["Indian Railways", ["indian railways uts", "indian railways"]],
  ["LivPure", ["livpure smart homes", "livpure"]],
  ["HundredX", ["hundredx school", "hundredx"]],
]

// ── Payment gateway prefixes ────────────────────────────────────────────────
// "Paytm*Swiggy Noida" → the merchant is Swiggy, not Paytm.
// Strip these before alias resolution.
const GATEWAY_PREFIX = /^(paytm|payu|razorpay|cashfree|billdesk|easebuzz|rbl|pyu)\*/i

// ── Build lookup index ──────────────────────────────────────────────────────

/** Pre-built lowercase alias → canonical map for O(1) lookups */
const aliasIndex: { alias: string; canonical: string }[] = []

for (const [canonical, aliases] of MERCHANT_ALIASES) {
  for (const alias of aliases) {
    aliasIndex.push({ alias: alias.toLowerCase(), canonical })
  }
}

// The merchant dictionary's narration aliases (#322) join the hand-built table above.
for (const m of dictionaryMerchants) {
  for (const alias of m.aliases)
    aliasIndex.push({ alias: alias.toLowerCase(), canonical: m.cleanName })
}

// Sort by alias length descending — longer (more specific) matches first
aliasIndex.sort((a, b) => b.alias.length - a.alias.length)

/**
 * Resolve a merchant name to its canonical form using the alias table.
 *
 * Returns the canonical name if found, or null if no alias matches.
 * Matching is case-insensitive substring — if the normalised name
 * contains any known alias, it resolves.
 *
 * @param name - Raw or normalised merchant name
 */
export function resolveAlias(name: string): string | null {
  let lower = name.toLowerCase()

  // Strip gateway prefix: "paytm*swiggy noida" → "swiggy noida"
  // This prevents "Paytm*Swiggy" from resolving to Paytm instead of Swiggy.
  const gwMatch = lower.match(GATEWAY_PREFIX)
  if (gwMatch) {
    lower = lower.slice(gwMatch[0].length).trim()
  }

  for (const { alias, canonical } of aliasIndex) {
    if (alias.length <= 4) {
      // Short aliases need word-boundary matching to avoid "cred" matching "credit"
      // or "ola" matching "console". Check that the alias is a standalone word.
      const re = new RegExp(`\\b${alias}\\b`)
      if (re.test(lower)) return canonical
    } else {
      if (lower.includes(alias)) return canonical
    }
  }
  return null
}

/**
 * Check if two names resolve to the same canonical merchant.
 *
 * Returns true if both names resolve to the same alias AND that alias is not null.
 * Returns false if either name doesn't resolve (fall back to fuzzy matching).
 */
export function aliasesMatch(a: string, b: string): boolean {
  const resolvedA = resolveAlias(a)
  const resolvedB = resolveAlias(b)
  if (resolvedA === null || resolvedB === null) return false
  return resolvedA === resolvedB
}
